import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Direction_Enum, SummaryFeedbackType } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { UpdateSummaryFeedbackRequest } from '@weave/schema-gen-ts/dist/schemas/fax/summary/v1/fax_summary.pb';
import { FaxSummaryV1 } from '@frontend/api-fax-summary';
import { useTranslation } from '@frontend/i18n';
import { PopoverMenu, UsePopoverMenuResponse } from '@frontend/design-system';
import { AIOverviewChip } from './ai-overview-chip';
import { ContactText } from './contact-text';
import { FeedbackSection } from './feedback-section';
import { HeaderText } from './header-text';
import { popoverStyles, contentStyles } from './styles';
import { SummaryContent } from './summary-content';

export type FaxAiSummaryPopoverProps = {
  popoverProps: UsePopoverMenuResponse<HTMLDivElement>;
  contactName: string;
  contactNumber: string;
  direction: Direction_Enum;
  locationId: string;
  faxId: string;
  mediaId: string;
  openFeedbackModal: () => void;
  setFeedbackRequest: Dispatch<SetStateAction<UpdateSummaryFeedbackRequest | undefined>>;
  onLoadingChange: Dispatch<SetStateAction<boolean>>;
  onLoadSuccess?: Dispatch<SetStateAction<boolean>>;
};

export const FaxAiSummaryPopover = ({
  popoverProps,
  contactName,
  contactNumber,
  direction,
  locationId,
  faxId,
  mediaId,
  openFeedbackModal,
  setFeedbackRequest,
  onLoadingChange,
  onLoadSuccess,
}: FaxAiSummaryPopoverProps) => {
  const { t } = useTranslation('fax');
  const { getMenuProps } = popoverProps;

  const getErrorMessage = ({
    mediaId,
    faxId,
    locationId,
    isGenerateSummaryError,
  }: {
    mediaId: string;
    locationId: string;
    faxId: string;
    isGenerateSummaryError: boolean;
  }) => {
    if (!faxId || !mediaId) return t('Fax pages not found');
    if (!locationId) return t('Fax location not found');
    if (isGenerateSummaryError) return t('Error generating summary');
    return '';
  };

  const popoverMenuProps = getMenuProps();
  const isPopoverOpen = popoverMenuProps.isOpen;

  const {
    data,
    isError: isGenerateSummaryError,
    isLoading: isGenerateSummaryLoading,
    isFetching: isGenerateSummaryFetching,
    isFetched,
  } = FaxSummaryV1.Queries.useGenerateSummaryQuery(
    {
      mediaId,
      faxId,
      locationId,
      direction,
    },
    {
      enabled: !!mediaId && !!faxId && !!locationId && isPopoverOpen,
      retry: 1,
    }
  );
  const faxSummaryData = data?.summary;
  const faxSummaryId = data?.id;
  const feedbackType = data?.feedbackType;
  const invalidateGeneratedFaxSummary = FaxSummaryV1.Hooks.useInvalidateGenerateSummary();
  const updateGenerateSummaryCache = FaxSummaryV1.Hooks.useUpdateGenerateSummaryCache();

  const isLoading =
    (isGenerateSummaryLoading || isGenerateSummaryFetching) &&
    !isFetched &&
    !faxSummaryData?.summary &&
    !faxSummaryData?.title;

  const isError = !locationId || !faxId || !mediaId || isGenerateSummaryError;
  const errorMessage = getErrorMessage({ locationId, faxId, mediaId, isGenerateSummaryError });

  const faxSummaryText = faxSummaryData?.summary ?? t('Summary is not available');
  const faxSummaryHeader = faxSummaryData?.title;

  const faxSummaryContact =
    (contactName && contactName.trim()) ||
    (direction === Direction_Enum.INBOUND
      ? faxSummaryData?.senderContact?.name?.trim()
      : faxSummaryData?.recipientContact?.name?.trim()) ||
    contactNumber ||
    t('Unknown Contact');

  const faxSummaryRef = useRef(faxSummaryData);
  faxSummaryRef.current = faxSummaryData;

  useEffect(() => {
    return () => {
      const currentFaxSummary = faxSummaryRef.current;
      if ((!currentFaxSummary || !currentFaxSummary?.summary) && faxId && locationId && mediaId) {
        invalidateGeneratedFaxSummary({ locationId, faxId, mediaId });
      }
    };
  }, []);

  const canShowFeedback = !isError && (isLoading || feedbackType === SummaryFeedbackType.UNSPECIFIED);
  const disableFeedback = isLoading || !faxSummaryId || !faxSummaryData?.summary;

  const onFeedbackSubmitSuccess = (feedbackType: SummaryFeedbackType) => {
    popoverProps.close();
    updateGenerateSummaryCache({ locationId, faxId, mediaId, direction }, (cacheData) => {
      return {
        ...cacheData,
        feedbackType,
      };
    });
  };

  useEffect(() => {
    onLoadingChange?.(isLoading);
    return () => {
      onLoadingChange?.(false);
    };
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    onLoadSuccess?.(!!faxSummaryData?.summary && !!faxSummaryHeader);
  }, [faxSummaryHeader, faxSummaryData?.summary, onLoadSuccess]);

  return (
    <PopoverMenu {...popoverMenuProps} css={popoverStyles}>
      <div css={contentStyles}>
        <AIOverviewChip />

        <HeaderText text={faxSummaryHeader || ''} loading={isLoading} />

        <ContactText name={faxSummaryContact} direction={direction} loading={isLoading} />

        <SummaryContent
          isError={isError}
          errorMessage={errorMessage}
          isLoading={isLoading}
          summaryText={faxSummaryText}
        />

        {canShowFeedback && (
          <FeedbackSection
            faxSummaryId={faxSummaryId}
            disableFeedback={disableFeedback}
            locationId={locationId}
            feedbackSubmitSuccessCb={onFeedbackSubmitSuccess}
            openFeedbackModal={openFeedbackModal}
            setFeedbackRequest={setFeedbackRequest}
          />
        )}
      </div>
    </PopoverMenu>
  );
};
